import type { BuildingTranslations } from "../../types/buildings";
import { buildingCore } from "./building-sections/core";
import { buildingDetails } from "./building-sections/details";
import { technicalSystems } from "./building-sections/technical-systems";
import { finishes } from "./building-sections/finishes";
import { structuralMaterials } from "./building-sections/structural-materials";
import { fireSafety } from "./building-sections/fire-safety";
import { roomsSection } from "./building-sections/rooms";
import { chat } from "./building-sections/chat";
import { sidebar } from "./building-sections/sidebar";
import { buildingList } from "./building-sections/list";
import { buildingDataWidget } from "./building-sections/building-data-widget";
import { ifcViewer } from "./building-sections/ifc-viewer";

// Combine all building section translations
export const buildings: BuildingTranslations = {
  // Core building properties
  ...buildingCore,

  // Additional details and form
  ...buildingDetails,

  // Technical systems
  technicalSystems,

  // Finishes
  finishes,

  // Structural materials
  structuralMaterials,

  // Fire safety
  fireSafety,

  // Rooms section
  roomsSection,

  // List translations
  list: buildingList,

  // Chat functionality
  chat,

  // Sidebar navigation
  sidebar,

  // Global building/project scope and its read-only context label
  scope: {
    label: "Ulatus",
    contextLabel: "Hoone ja projekti kontekst",
    selectProject: "Vali projekt",
    switchProject: "Vaheta projekti",
    searchPlaceholder: "Otsi projekte...",
    noProjects: "Projekte ei leitud.",
    loadFailed: "Selle hoone projekte ei õnnestunud laadida.",
    chooseProjectFolders: "Vali projekt, et näha selle kaustu.",
  },

  // Projects-view dashboard (single-project perspective)
  projectsDashboard: {
    title: "Projekti töölaud",
    myTasks: {
      title: "Minu ülesanded",
      dueWithin: "Tähtaeg",
      window: {
        oneWeek: "1 nädal",
        twoWeeks: "2 nädalat",
        oneMonth: "1 kuu",
      },
      empty: "Selles ajavahemikus tähtaegu ei ole",
      viewAll: "Vaata kõiki minu ülesandeid",
      documentsCount: "Seotud dokumente: {count}",
      commentsCount: "Kommentaare: {count}",
    },
    uploadedToday: {
      title: "Täna üles laaditud",
      viewAll: "Vaata kõiki",
      empty: "Täna pole veel midagi üles laaditud.",
    },
    recentChanges: {
      title: "Mis on uut",
      timeframeLabel: "Viimased",
      timeframe: {
        day: "24 h",
        threeDays: "72 h",
        week: "7 päeva",
        month: "30 päeva",
      },
      summary: {
        newIssues: "Uued ülesanded",
        completedIssues: "Lõpetatud ülesanded",
        updatedIssues: "Muudetud ülesanded",
        comments: "Kommentaarid",
        newDocuments: "Uued dokumendid",
        newVersions: "Uued versioonid",
      },
      nothingChanged: "Selles ajavahemikus pole midagi muutunud.",
      moreItems: "+{count} veel",
      documentsPartialAccess:
        "Dokumentide muudatusi ei kuvata, sest teil puudub juurdepääs kõigile selle projekti dokumentidele.",
      atLeastHint: "Vähemalt nii palju — tegevust oli liiga palju, et kõike kokku lugeda.",
      loadFailed: "Muudatusi ei õnnestunud laadida.",
      forYou: {
        title: "Sulle",
        mentioned: "{actor} mainis sind: {subject}",
        assigned: "{actor} määras sulle ülesande {subject}",
        discipline: "Uus vastutusalas {discipline}: {subject}",
        someone: "Keegi",
        more: "+{count} veel",
      },
    },
  },

  // Building Data sidebar widget
  buildingDataWidget,

  // 3D model (IFC) viewer surface (WHA-3689)
  ifcViewer,

  // Navigation actions
  viewDashboard: "Vaata töölaua",
  viewBuilding: "Vaata hoonet",
  mapError: "Viga kaardi laadimisel.",
  geocodeError: "Aadressi määramine asukoha põhjal ebaõnnestus.",
  projectionsPage: {
    title: "Eelarve prognoosid",
    chartTitle: "Igakuised prognoosid",
  },
  budgetPage: {
    loading: "Laen eelarve andmeid...",
    loadError: "Eelarve andmete laadimine eba\u00F5nnestus",
    saveError: "Eelarve andmete salvestamine eba\u00F5nnestus",
  },
  myBuildings: "Minu hooned",
  myArchivedBuildings: "Minu arhiveeritud hooned",
  archive: "Arhiveeri",
  restore: "Taasta",
  archived: "Hoone arhiveeritud",
  movedToArchive: "Hoone on arhiivi viidud.",
  archiveError: "Hoone arhiveerimine eba\u00F5nnestus. Palun proovige uuesti.",
  restored: "Hoone taastatud",
  restoredFromArchive: "Hoone on arhiivist taastatud.",
  restoreError: "Hoone taastamine eba\u00F5nnestus. Palun proovige uuesti.",
  renameError: "Hoone \u00FCmbernimetamine eba\u00F5nnestus. Palun proovige uuesti.",
  archiveTitle: "Arhiveeri hoone",
  restoreTitle: "Taasta hoone",
  archiveConfirm:
    "Kas olete kindel, et soovite selle hoone arhiveerida? Saate selle hiljem arhiivist taastada.",
  restoreConfirm:
    "Kas olete kindel, et soovite selle hoone taastada? See viiakse tagasi aktiivsete hoonete hulka.",
  selectCertificateType: "Palun valige sertifikaadi t\u00FC\u00FCp",
  buildingInformation: "Hoone teave",
  addRenovationDate: "Lisa renoveerimise kuup\u00E4ev...",
  selectBuildingType: "Vali hoone t\u00FC\u00FCp",
  grossArea: "Brutopind (m\u00B2)",
  heatedArea: "K\u00F6etav pind (m\u00B2)",
  leasableArea: "\u00DC\u00FCritav pind (m\u00B2)",
  residentialArea: "Elamispind (m\u00B2)",
  commercialArea: "\u00C4ripind (m\u00B2)",
  areaValidationError: "Pindala valideerimise t\u00F5rge",
  floorAreaRatio: "P\u00F5randapinna suhtarv",
  buildingRightTotal: "Ehitus\u00F5igus kokku (brutopind) (m\u00B2)",
  buildingRightLeft: "Ehitus\u00F5iguse j\u00E4\u00E4k (m\u00B2)",
  bicycleParkingSpots: "Jalgrattaparkimiskohad",
  evChargingSpots: "Elektriautode laadimiskohad",
  selectOrgBeforeCreating: "Palun valige organisatsioon enne hoone loomist.",
  creatingBuilding: "Hoone loomine...",
  nameMinLength: "Hoone nimi peab olema v\u00E4hemalt 2 t\u00E4hem\u00E4rki.",
  addressMinLength: "Aadress peab olema v\u00E4hemalt 5 t\u00E4hem\u00E4rki.",
};
